import { createElement, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Col, Dropdown, Menu, Row, Tag } from 'antd';
import { capitalCase } from 'change-case';

import { BillingInterval, PLAN_CONFIG, PlanKey } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { DropdownButton, DropdownMenuPanel } from '../../dropdowns';
import { LearnMoreFooter } from '../../layouts/footers';
import { MiddleDot } from '../../typography';
import { getSeatPrice } from './pricing';

export interface PlanSelectorProps {
    interval: BillingInterval;
    onChange: (plan: PlanKey) => void;
    value: PlanKey;
}

const formatPrice = (value: number) =>
    new Intl.NumberFormat('en-US', {
        currency: 'USD',
        maximumFractionDigits: 2,
        style: 'currency',
    }).format(value);

const PlanSelector: FC<PlanSelectorProps> = ({ interval, onChange, value }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const renderSeatPrice = useCallback(
        (price: number | null): ReactNode => {
            if (price === null) {
                return 'Custom Pricing';
            }

            const monthlyPrice = interval === 'annual' ? price / 12 : price;

            return (
                <>
                    {formatPrice(monthlyPrice)}
                    /active seat/month
                </>
            );
        },
        [interval],
    );
    const menu = useMemo(
        () => (
            <DropdownMenuPanel onClick={(event) => onChange(event.key as PlanKey)} selectedKeys={[value]}>
                <div style={{ marginBottom: '0.375rem', padding: '0 0.75rem' }}>
                    <small style={{ color: token.colorTextTertiary }}>Plans ({capitalCase(interval)} Pricing)</small>
                </div>
                {Object.values(PLAN_CONFIG).map((plan) => {
                    const price = getSeatPrice(plan.key, interval);

                    return (
                        <Menu.Item key={plan.key}>
                            <Row>
                                <Col flex='none'>
                                    <span style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }}>
                                        <Avatar
                                            icon={
                                                <span style={{ color: token.colorTextTertiary }}>
                                                    {createElement(plan.icon, {
                                                        size: '75%',
                                                        style: { verticalAlign: '-0.15rem' },
                                                    })}
                                                </span>
                                            }
                                            shape='square'
                                            size={16}
                                            style={{
                                                border: `1px solid ${token.colorBorder}`,
                                                marginRight: '0.125rem',
                                                marginTop: '-0.35rem',
                                            }}
                                        />
                                    </span>
                                    {plan.label}
                                    <MiddleDot horizontalMargin='0.5rem' />
                                    <small style={{ color: token.colorTextTertiary }}>{renderSeatPrice(price)}</small>
                                </Col>
                                <Col flex='auto' />
                                <Col flex='none'>
                                    {plan.displayDiscountPercent?.[interval] ? (
                                        <Tag color='green' style={{ borderColor: 'transparent', marginRight: 0 }}>
                                            {plan.displayDiscountPercent[interval]}% Off
                                        </Tag>
                                    ) : null}
                                </Col>
                            </Row>
                            <div>
                                <small style={{ color: token.colorTextTertiary }}>{plan.description}</small>
                            </div>
                        </Menu.Item>
                    );
                })}
                <LearnMoreFooter href='https://countercyclical.com/pricing' topic='our Plans' variant='dropdown' />
            </DropdownMenuPanel>
        ),
        [interval, onChange, renderSeatPrice, token.colorBorder, token.colorTextTertiary, value],
    );
    const activePlan = PLAN_CONFIG[value];
    const activePrice = getSeatPrice(value, interval);
    const [width, setWidth] = useState<number>();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            entries.forEach((entry) => setWidth(entry.contentRect.width));
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    return (
        <Dropdown
            popupRender={() => menu}
            overlayStyle={{ maxWidth: '100%', width }}
            placement='bottomLeft'
            trigger={['click']}
        >
            <div ref={ref}>
                <DropdownButton block>
                    <Row align='middle' justify='space-between' style={{ width: '100%' }} wrap={false}>
                        <Col flex='none'>
                            <Avatar
                                icon={
                                    <span style={{ color: token.colorTextTertiary }}>
                                        {createElement(activePlan.icon, {
                                            size: '75%',
                                            style: { verticalAlign: '-0.15rem' },
                                        })}
                                    </span>
                                }
                                shape='square'
                                size={16}
                                style={{
                                    border: `1px solid ${token.colorBorder}`,
                                    marginRight: '0.375rem',
                                    marginTop: '-2px',
                                }}
                            />
                            {activePlan.label}
                            <MiddleDot horizontalMargin='0.5rem' />
                            <small style={{ color: token.colorTextTertiary }}>{renderSeatPrice(activePrice)}</small>
                        </Col>
                    </Row>
                </DropdownButton>
            </div>
        </Dropdown>
    );
};

export default PlanSelector;
